import { Link, useNavigate, useParams } from 'react-router-dom';
import JsonLd from '@/components/base/JsonLd';
import { characters } from '@/mocks/characters';

const siteUrl = 'https://starvoice3.github.io';

const factionColor: Record<string, string> = {
  守护者: 'bg-secondary-100 text-secondary-900',
  复活社: 'bg-accent-100 text-accent-900',
  守护甜心: 'bg-primary-100 text-primary-700',
};

export default function CharacterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const character = characters.find((c) => c.id === id);

  if (!character) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400" aria-hidden="true"></i>
        <h1 className="font-heading text-xl font-bold text-foreground-900">未找到该角色</h1>
        <p className="text-sm text-foreground-600">你可能访问了不存在的角色链接，返回角色大全看看吧。</p>
        <button
          type="button"
          onClick={() => navigate('/characters')}
          className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          返回角色大全
        </button>
      </div>
    );
  }

  const related = characters.filter((c) => c.id !== character.id).slice(0, 3);
  const infoItems = [
    { label: '身份', value: character.title },
    { label: '性别', value: character.gender },
    { label: '生日', value: character.birthday },
    { label: '星座', value: character.zodiac },
    { label: '配音演员', value: character.cv },
    { label: '罗马音', value: character.enName },
  ];

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${character.name}角色资料`,
      name: `${character.name} - 守护甜心动漫角色大全`,
      description: character.short,
      image: character.image,
      inLanguage: 'zh-CN',
      author: { '@type': 'Organization', name: '守护甜心档案' },
      publisher: { '@type': 'Organization', name: '守护甜心档案' },
      mainEntityOfPage: `${siteUrl}/characters/${character.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '角色大全', item: `${siteUrl}/characters` },
        { '@type': 'ListItem', position: 3, name: character.name, item: `${siteUrl}/characters/${character.id}` },
      ],
    },
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
      <JsonLd data={schema} />
      <nav aria-label="面包屑" className="mb-6">
        <ol className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <li>
            <Link to="/" className="transition-colors hover:text-primary-600">
              首页
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li>
            <Link to="/characters" className="transition-colors hover:text-primary-600">
              角色大全
            </Link>
          </li>
          <li aria-hidden="true">
            <i className="ri-arrow-right-s-line" />
          </li>
          <li aria-current="page" className="font-medium text-foreground-800">
            {character.name}
          </li>
        </ol>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[380px_1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="overflow-hidden rounded-2xl border border-background-200/80 bg-background-50">
            <div className="relative h-96 w-full overflow-hidden bg-background-100 lg:h-[440px]">
              <img
                src={character.image}
                alt={`守护甜心动漫角色 ${character.name} 立绘`}
                className="h-full w-full object-cover object-top"
                loading="eager"
              />
              <span
                className={`absolute left-4 top-4 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${factionColor[character.faction]}`}
              >
                {character.faction}
              </span>
            </div>
            <div className="p-5">
              <blockquote className="border-l-2 border-primary-400 pl-3 text-sm italic leading-6 text-primary-600">
                「{character.quote}」
              </blockquote>
              <dl className="mt-5 space-y-3">
                {infoItems.map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-3 border-b border-background-200/70 pb-2.5">
                    <dt className="text-sm text-foreground-500">{item.label}</dt>
                    <dd className="text-right text-sm font-medium text-foreground-800">{item.value}</dd>
                  </div>
                ))}
              </dl>
              <Link
                to="/voice-actors"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-mic-line" aria-hidden="true"></i>
                查看完整声优表
              </Link>
            </div>
          </div>
        </aside>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-3xl font-black text-foreground-900 md:text-4xl">{character.name}</h1>
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-700">
              {character.enName}
            </span>
          </div>
          <p className="mt-3 font-heading text-lg font-bold text-primary-600">{character.title}</p>
          <p className="mt-3 text-base leading-7 text-foreground-700">{character.short}</p>

          <h2 className="mt-8 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
            <i className="ri-book-open-line text-primary-500" aria-hidden="true"></i>
            角色背景故事
          </h2>
          <div className="mt-4 space-y-4">
            {character.story.map((para, i) => (
              <p key={i} className="text-[15px] leading-8 text-foreground-700">
                {para}
              </p>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-background-200/80 bg-background-100/70 p-5 md:p-6">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-2xl">
                <img
                  src={character.chara.image}
                  alt={`${character.name} 的守护甜心 ${character.chara.name}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-900">
                  <i className="ri-heart-3-line text-primary-500" aria-hidden="true"></i>
                  守护甜心：{character.chara.name}
                </h2>
                <p className="mt-1.5 text-sm leading-6 text-foreground-600">{character.chara.description}</p>
              </div>
            </div>
          </div>

          <h2 className="mt-10 flex items-center gap-2 font-heading text-xl font-bold text-foreground-900">
            <i className="ri-team-line text-primary-500" aria-hidden="true"></i>
            更多角色
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((c) => (
              <Link
                key={c.id}
                to={`/characters/${c.id}`}
                className="group overflow-hidden rounded-2xl border border-background-200/80 bg-background-50 transition-all hover:-translate-y-1 hover:border-primary-300"
              >
                <div className="h-40 w-full overflow-hidden bg-background-100">
                  <img
                    src={c.image}
                    alt={`守护甜心角色 ${c.name}`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold text-foreground-900">{c.name}</h3>
                  <p className="mt-1 truncate text-xs text-foreground-500">
                    配音：{c.cv} · {c.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}